import type {
  ChatChannel,
  PrivatePlayer,
  PublicPlayer,
  RoomState,
  VoiceAccess,
  VoiceChannel,
  VoiceParticipant,
  VoiceSettings,
} from "@ma-soi/game-core";
import { useCallback, useEffect, useState } from "react";
import type { ChatMessage } from "../components/ChatPanel";
import {
  apiRequest,
  errorMessage,
  getResumeToken,
  requestId,
  roomWebSocket,
  saveResumeToken,
} from "../lib";

export interface EventLog {
  id: string;
  message: string;
  type: string;
  createdAtMs: number;
}

export interface RoomSnapshot {
  type: "snapshot";
  uid: string;
  room: RoomState;
  players: PublicPlayer[];
  privatePlayer: PrivatePlayer | null;
  events: EventLog[];
  messages: ChatMessage[];
  channel: ChatChannel;
  voice: {
    access: VoiceAccess;
    participants: VoiceParticipant[];
    settings: VoiceSettings;
    hostMutedPlayerIds: string[];
  };
  serverNowMs: number;
}

interface RoomClosedMessage {
  type: "roomClosed";
  reason: string;
}

export interface VoiceSignalMessage {
  type: "voice:signal";
  fromPlayerId: string;
  signalType: "offer" | "answer" | "ice-candidate";
  channel: Exclude<VoiceChannel, "none">;
  payload: unknown;
  serverNowMs: number;
  receivedAtMs: number;
}

interface VoiceErrorMessage {
  type: "voice:error";
  error: string;
}

export function useRoomConnection(roomId: string) {
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [needsJoin, setNeedsJoin] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roomClosed, setRoomClosed] = useState<string | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [voiceSignal, setVoiceSignal] = useState<VoiceSignalMessage | null>(null);

  const sendSocketMessage = useCallback((message: unknown) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }, [socket]);

  useEffect(() => {
    if (!roomId) return;
    let disposed = false;
    let socket: WebSocket | null = null;
    let retryTimer = 0;
    let pingTimer = 0;
    let retryDelay = 750;
    let closedByHost = false;
    let connecting = false;

    const scheduleReconnect = () => {
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => {
        retryDelay = Math.min(retryDelay * 2, 10_000);
        void connect();
      }, retryDelay);
    };

    const tryResumeSeat = async () => {
      const resumeToken = getResumeToken(roomId);
      if (!resumeToken) return false;
      try {
        const result = await apiRequest<{ resumeToken: string }>(`/api/rooms/${roomId}/rejoin`, {
          method: "POST",
          body: JSON.stringify({ requestId: requestId(), resumeToken }),
        });
        saveResumeToken(roomId, result.resumeToken);
        setNeedsJoin(false);
        return true;
      } catch {
        return false;
      }
    };

    const connect = async () => {
      if (connecting) return;
      connecting = true;
      try {
        const initial = await apiRequest<RoomSnapshot>(`/api/rooms/${roomId}/snapshot`);
        if (disposed) return;
        setSnapshot(initial);
        setNeedsJoin(false);
        socket = await roomWebSocket(roomId);
        const activeSocket = socket;
        let opened = false;
        activeSocket.addEventListener("open", () => {
          opened = true;
          retryDelay = 750;
          setConnected(true);
          setSocket(activeSocket);
          setError(null);
          pingTimer = window.setInterval(() => {
            if (activeSocket.readyState === WebSocket.OPEN) activeSocket.send("ping");
          }, 25_000);
        });
        activeSocket.addEventListener("message", (event) => {
          try {
            const data = JSON.parse(String(event.data)) as RoomSnapshot | RoomClosedMessage | VoiceSignalMessage | VoiceErrorMessage | { type: string };
            if (data.type === "snapshot") {
              setSnapshot(data as RoomSnapshot);
            } else if (data.type === "roomClosed") {
              closedByHost = true;
              setRoomClosed((data as RoomClosedMessage).reason);
              activeSocket.close(1000, "room-closed");
            } else if (data.type === "voice:signal") {
              setVoiceSignal({ ...(data as VoiceSignalMessage), receivedAtMs: Date.now() });
            } else if (data.type === "voice:error") {
              setError((data as VoiceErrorMessage).error);
            }
          } catch {
            // Bỏ qua gói tin không hợp lệ.
          }
        });
        activeSocket.addEventListener("close", () => {
          window.clearInterval(pingTimer);
          setConnected(false);
          setSocket((current) => (current === activeSocket ? null : current));
          if (!disposed && !closedByHost) {
            void (async () => {
              if (!opened) await tryResumeSeat();
              scheduleReconnect();
            })();
          }
        });
        activeSocket.addEventListener("error", () => {
          setError("Kết nối thời gian thực bị gián đoạn. Đang thử nối lại…");
        });
      } catch (reason) {
        if (disposed) return;
        const message = errorMessage(reason);
        if (/không tồn tại/i.test(message)) {
          setRoomClosed("Ngôi làng không còn tồn tại.");
          setSnapshot(null);
          setError(null);
          return;
        }
        if (/không thuộc phòng/i.test(message)) {
          if (await tryResumeSeat()) {
            scheduleReconnect();
          } else {
            setNeedsJoin(true);
            setSnapshot(null);
            setError(null);
          }
          return;
        }
        setError(message);
        scheduleReconnect();
      } finally {
        connecting = false;
      }
    };

    void connect();
    return () => {
      disposed = true;
      window.clearTimeout(retryTimer);
      window.clearInterval(pingTimer);
      socket?.close(1000, "page-unloaded");
    };
  }, [roomId]);

  return { snapshot, needsJoin, connected, error, roomClosed, socket, sendSocketMessage, voiceSignal };
}
